import EditorController from '@/editor/components/EditorController'
import SwitchController from '@/common/atoms/SwitchController'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  UseModalProps,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import {
  Governance_Mode_Enum,
  Thread_Activity_Type_Enum,
  Thread_Status_Enum,
  Thread_Type_Enum,
  useCreateThreadActivityMutation,
  useCreateThreadMutation,
  useUpdateThreadActivityMutation,
} from '@gql'
import { yupResolver } from '@hookform/resolvers/yup'
import ParticipantsNumber from '@/participants/components/ParticipantsNumber'
import CircleFormController from '@/circle/components/CircleFormController'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { useNavigateOrg } from '@/org/hooks/useNavigateOrg'
import { getOpenProposalDecisionModes } from '@rolebase/shared/helpers/getOpenProposalDecisionModes'
import {
  defaultProposalDurationDays,
  ProposalDecisionMode,
  ProposalLog,
  ProposalVotersScope,
  ThreadActivityDataProposal,
} from '@rolebase/shared/model/proposal'
import { ThreadActivityProposalFragment } from '@rolebase/shared/model/thread_activity'
import { addDays, format } from 'date-fns'
import useDateLocale from '@/common/hooks/useDateLocale'
import { MeetingContext } from '@/meeting/contexts/MeetingContext'
import { ThreadContext } from '@/thread/contexts/ThreadContext'
import React, { useContext, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { OrgChartIcon } from 'src/icons'
import * as yup from 'yup'
import ProposalLogsPreview from '../components/ProposalLogsPreview'
import ProposalGraphEditorModal from './ProposalGraphEditorModal'
import ProposalNonVotersNotice from '../components/ProposalNonVotersNotice'
import useProposalVoters from '../hooks/useProposalVoters'

interface Props extends UseModalProps {
  threadId?: string // To create a proposal in an existing thread
  // When there is no thread yet (e.g. opened from a role's Security menu), a
  // thread is created on the fly. This pre-fills the circle picker.
  circleId?: string
  activity?: ThreadActivityProposalFragment // To update
  defaults?: Partial<ThreadActivityDataProposal> // To prefill a new proposal
}

interface Values {
  title: string
  circleId: string
  description: string
  decisionMode: ProposalDecisionMode
  votersScope: ProposalVotersScope
  allowAbstain: boolean
  showVoters: boolean
  earlyResolution: boolean
  archiveTargetDecision: boolean
}

const resolver = yupResolver(
  yup.object().shape({
    title: yup.string().required(),
  })
)

export default function ProposalModal({
  threadId,
  circleId,
  activity,
  defaults,
  ...modalProps
}: Props) {
  const { t } = useTranslation()
  const { circle } = useContext(ThreadContext) ?? {}
  const { orgId, org, orgData } = useOrgContext()
  const currentMember = useCurrentMember()

  // The organization opens the decision modes and fixes how long a proposal
  // lasts. The author picks among what is open, and never sets the deadline.
  const decisionModes = getOpenProposalDecisionModes(
    org?.proposalDecisionModes,
    activity?.data.decisionMode
  )
  // Written from inside a meeting: the only mode offered is the meeting itself.
  // An asynchronous vote started in session would have to designate who votes
  // and then wait for the absent, whereas a meeting decides with the people in
  // the room. Whether the other modes could ever mean something in session is
  // left open (Yohan, 06/08/2026) — the choice is closed, not the question.
  const meetingState = useContext(MeetingContext)
  const writtenInMeeting = !!meetingState?.meeting

  const asyncGovernanceEnabled = !!org?.asyncGovernanceEnabled
  const durationDays = org?.proposalDurationDays ?? defaultProposalDurationDays
  const dateLocale = useDateLocale()

  // Fixed when the modal opens, so what the author reads is what is stored.
  // Editing an existing proposal never restarts its clock.
  const resolutionDate = useMemo(
    () =>
      activity?.data.resolutionDate
        ? new Date(activity.data.resolutionDate)
        : addDays(new Date(), durationDays),
    [activity, durationDays]
  )

  // Strict governance: only the circle decides (D6). Letting the author narrow
  // the audience to the people on the tension would let them pick a friendlier
  // room than the one the mode designates.
  const isStrictGovernance =
    orgData?.governanceMode === Governance_Mode_Enum.Strict
  const navigateOrg = useNavigateOrg()
  const [createActivity] = useCreateThreadActivityMutation()
  const [updateActivity] = useUpdateThreadActivityMutation()
  const [createThread] = useCreateThreadMutation()

  // When there is no existing thread/activity, a thread is created on submit.
  const needsThread = !activity && !threadId

  const base = activity?.data || defaults

  // Editing a proposal that already runs on an asynchronous mode keeps that
  // mode listed, even inside a meeting: hiding it would leave the field on a
  // value absent from its own list, silently switching the proposal to another
  // mode on the next save.
  const editingAsyncProposal =
    !!base?.decisionMode && base.decisionMode !== 'meeting'
  const offerAsyncModes =
    asyncGovernanceEnabled && (!writtenInMeeting || editingAsyncProposal)
  const [logs, setLogs] = useState<ProposalLog[]>(base?.logs || [])
  const editor = useDisclosure()

  const form = useForm<Values>({
    resolver,
    defaultValues: {
      title: base?.title || '',
      circleId: circleId || '',
      description: base?.description || '',
      // Without asynchronous voting, deciding in a meeting is the only path, so
      // it is what the form starts on. Before D6 the field was simply hidden
      // and every proposal silently fell back to 'consent' — a vote nobody had
      // chosen, in an organization that had turned voting off.
      decisionMode:
        base?.decisionMode || (offerAsyncModes ? 'consent' : 'meeting'),
      votersScope: isStrictGovernance
        ? 'circle'
        : base?.votersScope || 'circle',
      allowAbstain: base?.allowAbstain ?? false,
      showVoters: base?.showVoters ?? true,
      earlyResolution: base?.earlyResolution ?? true,
      archiveTargetDecision: base?.archiveTargetDecision ?? false,
    },
  })
  const {
    handleSubmit,
    register,
    control,
    watch,
    setError,
    formState: { errors },
  } = form

  const votersScope = watch('votersScope')
  const decisionMode = watch('decisionMode')
  const selectedCircleId = watch('circleId')

  // The policy this proposal aims at, if any. It is fixed when the modal opens
  // (it comes from the policy the author started from) and never picked here.
  const targetDecisionId = base?.targetDecisionId ?? null
  const archiveTargetDecision = watch('archiveTargetDecision')

  // The circle that will decide, once settled. Only a link tension has one to
  // settle, and it is settled by the changes made in the editor.
  const [decidingCircleId, setDecidingCircleId] = useState<string | null>(
    base?.decidingCircleId ?? null
  )

  // Circle used by the org-chart editor and voters: the thread's circle,
  // otherwise the one picked (or pre-filled) when creating a thread on the fly.
  const editorCircleId = circle?.id || selectedCircleId || undefined
  const voters = useProposalVoters(votersScope, editorCircleId, decidingCircleId)

  const onSubmit = handleSubmit(async (values) => {
    // Decided in the room: nothing is counted and nothing expires.
    const decidedInMeeting = values.decisionMode === 'meeting'
    const data = {
      title: values.title,
      description: values.description,
      decisionMode: values.decisionMode,
      votersScope: values.votersScope,
      allowAbstain:
        values.decisionMode === 'consent' || decidedInMeeting
          ? false
          : values.allowAbstain,
      showVoters: values.showVoters,
      earlyResolution: decidedInMeeting ? false : values.earlyResolution,
      // An asynchronous proposal has a deadline, and it comes from the
      // organization. It is what makes "silence is not consent, until the
      // deadline" true: with no deadline, a proposal where someone stays silent
      // waited forever.
      //
      // A meeting proposal has none, on purpose: the cron filters on this very
      // field, so leaving it null is what guarantees it is never resolved
      // behind anyone's back. A meeting that goes by without a decision changes
      // nothing at all (D6, A1 and A7).
      resolutionDate: decidedInMeeting ? null : resolutionDate.toISOString(),
      logs,
      status: activity?.data.status || ('inProgress' as const),
      resolvedAt: activity?.data.resolvedAt || null,
      appliedDecisionId: activity?.data.appliedDecisionId || null,
      // The policy this proposal is about, if any. Carried through edits like
      // logs are, otherwise re-opening the proposal would silently turn it back
      // into one that creates a second policy (GOUV-14).
      targetDecisionId,
      // Only ever true alongside a target: on its own the flag would describe
      // archiving nothing, and the resolution would silently create a policy.
      archiveTargetDecision: targetDecisionId
        ? values.archiveTargetDecision
        : false,
      applyError: activity?.data.applyError ?? null,
      decidingCircleId,
    }

    if (activity) {
      await updateActivity({ variables: { id: activity.id, values: { data } } })
    } else {
      if (!currentMember) return
      // Use the given thread, or create one named after the proposal.
      let targetThreadId = threadId
      const createdThread = !targetThreadId
      if (!targetThreadId) {
        if (!values.circleId) {
          setError('circleId', { message: t('ProposalModal.circleRequired') })
          return
        }
        if (!orgId || !currentMember) return
        const { data: threadData } = await createThread({
          variables: {
            values: {
              orgId,
              title: values.title,
              circleId: values.circleId,
              initiatorMemberId: currentMember.id,
              status: Thread_Status_Enum.Active,
              // A proposal always carries a governance change.
              type: Thread_Type_Enum.Governance,
            },
          },
        })
        targetThreadId = threadData?.insert_thread_one?.id
      }
      if (!targetThreadId) return
      await createActivity({
        variables: {
          values: {
            threadId: targetThreadId,
            memberId: currentMember.id,
            type: Thread_Activity_Type_Enum.Proposal,
            data,
          },
        },
      })
      // When the thread was created on the fly, open its page.
      if (createdThread) {
        navigateOrg(`threads/${targetThreadId}`)
      }
    }
    modalProps.onClose()
  })

  return (
    <Modal
      size="2xl"
      {...modalProps}
      // Drop the scroll lock while the full-screen org-chart editor is open:
      // its panels are portaled outside React's #root event root, where
      // react-remove-scroll can't whitelist their wheel scrolling, so any lock
      // (this one included) blocks it. See ProposalGraphEditorModal.
      blockScrollOnMount={!editor.isOpen}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(
            activity
              ? 'ProposalModal.headingEdit'
              : 'ProposalModal.headingCreate'
          )}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <FormProvider {...form}>
            <form onSubmit={onSubmit}>
              <VStack spacing={5} align="stretch">
                <FormControl isInvalid={!!errors.title}>
                  <FormLabel>{t('ProposalModal.title')}</FormLabel>
                  <Input
                    {...register('title')}
                    placeholder={t('ProposalModal.titlePlaceholder')}
                    autoFocus
                  />
                </FormControl>

                {needsThread && <CircleFormController writableOnly />}

                <FormControl>
                  <FormLabel>{t('ProposalModal.description')}</FormLabel>
                  <EditorController name="description" control={control} />
                </FormControl>

                {/* Aiming at a policy, say what approving does to it. Without
                  this switch, archiving had no path at all in strict
                  governance: the actions menu is hidden there, and a proposal
                  could only ever rewrite the policy it targeted (GOUV-14). */}
                {targetDecisionId && (
                  <FormControl>
                    <FormLabel>{t('ProposalModal.targetDecision')}</FormLabel>
                    <SwitchController
                      name="archiveTargetDecision"
                      control={control}
                    >
                      {t('ProposalModal.archiveTargetDecision')}
                    </SwitchController>
                    <FormHelperText>
                      {t(
                        archiveTargetDecision
                          ? 'ProposalModal.archiveTargetDecisionHelp'
                          : 'ProposalModal.editTargetDecisionHelp'
                      )}
                    </FormHelperText>
                  </FormControl>
                )}

                <Box>
                  <Flex align="center" mb={2}>
                    <Heading size="sm">{t('ProposalModal.orgChart')}</Heading>
                    <Box flex="1" />
                    <Button
                      leftIcon={<OrgChartIcon size={20} />}
                      onClick={editor.onOpen}
                    >
                      {t('ProposalModal.editOrgChart')}
                    </Button>
                  </Flex>
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    px={2}
                    py={1}
                    bg="white"
                    _dark={{ bg: 'blackAlpha.300' }}
                  >
                    <ProposalLogsPreview logs={logs} />
                  </Box>
                </Box>

                {/* Always shown, even without asynchronous voting: the list then
                  offers deciding in a meeting alone, which says out loud what
                  the organization has chosen. The group headings carry the
                  "asynchronous" meaning, so the modes below keep their plain
                  names. */}
                <FormControl>
                  <FormLabel>{t('ProposalModal.decisionMode')}</FormLabel>
                  <Select {...register('decisionMode')}>
                    <optgroup
                      label={t('ProposalModal.decisionModeGroupMeeting')}
                    >
                      <option value="meeting">
                        {t('ProposalModal.decisionMode_meeting')}
                      </option>
                    </optgroup>
                    {offerAsyncModes && (
                      <optgroup
                        label={t('ProposalModal.decisionModeGroupAsync')}
                      >
                        {decisionModes.map((mode) => (
                          <option key={mode} value={mode}>
                            {t(`ProposalModal.decisionMode_${mode}`)}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </Select>
                  {decisionMode === 'meeting' && (
                    <FormHelperText>
                      {t('ProposalModal.decisionModeMeetingHelp')}
                    </FormHelperText>
                  )}
                  {decisionMode !== 'consent' && decisionMode !== 'meeting' && (
                    <SwitchController
                      name="allowAbstain"
                      control={control}
                      mt={3}
                    >
                      {t('ProposalModal.allowAbstain')}
                    </SwitchController>
                  )}
                </FormControl>

                {/* Who votes, and whether their votes are revealed, only mean
                  something when there is a vote. A meeting decision is taken by
                  the circle in the room: designating an audience and promising
                  to reveal ballots would describe a ceremony that never runs. */}
                {decisionMode !== 'meeting' && (
                  <FormControl>
                    <FormLabel>{t('ProposalModal.voters')}</FormLabel>
                    <Flex align="center">
                      <Select
                        {...register('votersScope')}
                        maxW="280px"
                        isDisabled={isStrictGovernance}
                      >
                        {!isStrictGovernance && (
                          <option value="thread">
                            {t('ProposalModal.voters_thread')}
                          </option>
                        )}
                        <option value="circle">
                          {t('ProposalModal.voters_circle')}
                        </option>
                      </Select>
                      <ParticipantsNumber participants={voters} ml={3} />
                    </Flex>
                    <Box mt={3}>
                      <ProposalNonVotersNotice voters={voters} />
                    </Box>
                    <SwitchController
                      name="showVoters"
                      control={control}
                      mt={3}
                    >
                      {t('ProposalModal.showVoters')}
                    </SwitchController>
                  </FormControl>
                )}

                {/* Nothing here applies to a meeting decision: there is no clock
                  to run early and no deadline to announce. Showing a date the
                  proposal will never honour would be the lie this phase exists
                  to remove. */}
                {decisionMode !== 'meeting' && (
                  <FormControl>
                    <FormLabel>{t('ProposalModal.resolution')}</FormLabel>
                    <SwitchController name="earlyResolution" control={control}>
                      {t('ProposalModal.earlyResolution')}
                    </SwitchController>
                    {asyncGovernanceEnabled && (
                      <FormHelperText>
                        {t('ProposalModal.resolutionDateFixed', {
                          count: durationDays,
                          date: format(resolutionDate, 'PPPP', {
                            locale: dateLocale,
                          }),
                        })}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}

                <Box textAlign="right">
                  <Button colorScheme="blue" type="submit">
                    {t(activity ? 'common.save' : 'common.create')}
                  </Button>
                </Box>
              </VStack>
            </form>
          </FormProvider>
        </ModalBody>
      </ModalContent>

      {editor.isOpen && (
        <ProposalGraphEditorModal
          isOpen
          onClose={editor.onClose}
          logs={logs}
          circleId={editorCircleId}
          decidingCircleId={decidingCircleId}
          onChange={(nextLogs, nextDecidingCircleId) => {
            setLogs(nextLogs)
            setDecidingCircleId(nextDecidingCircleId)
          }}
        />
      )}
    </Modal>
  )
}
